<script setup>
import { onMounted, ref, computed } from 'vue';
import dayjs from 'dayjs';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';

import HeaderTitle from '@/components/layout/HeaderTitle';
import InvoiceService from '@/api/services/invoice.service';
import ClientService from '@/api/services/clients';
import { useUiStore } from '@/app/stores/uiStore';

const route = useRoute();
const router = useRouter();
const uiStore = useUiStore();

const invoice = ref(null);
const clients = ref([]);
const selectedClient = ref(null);
const pdfFileInput = ref(null);
const pdfDownloading = ref(false);

const notificationLabels = {
    notice_30: 'Aviso día 30 (cliente)',
    notice_35: 'Aviso día 35 (Carlos)',
    notice_60: 'Aviso día 60 (Paco)',
    notice_90: 'Aviso día 90 (Bárbara/burofax)',
};

onMounted(async () => {
    await loadInvoice();
    await loadClients();
});

const loadInvoice = async () => {
    uiStore.startLoading();
    InvoiceService.getInvoice(route.params.id)
        .then((res) => {
            invoice.value = res.data;
            if (invoice.value.client) {
                selectedClient.value = invoice.value.client['@id'] ?? invoice.value.client;
            }
        })
        .finally(() => uiStore.stopLoading());
};

const loadClients = async () => {
    ClientService.searchClients({ itemsPerPage: 200 })
        .then((res) => {
            clients.value = res.data['hydra:member'] ?? [];
        });
};

const markPaid = () => {
    ElMessageBox.confirm('¿Marcar esta factura como pagada?', 'Confirmar', {
        confirmButtonText: 'Sí, pagada',
        cancelButtonText: 'Cancelar',
        type: 'success',
    }).then(async () => {
        await InvoiceService.markPaid(invoice.value.id);
        ElMessage.success('Factura marcada como pagada');
        loadInvoice();
    }).catch(() => {});
};

const markPaymentPlan = () => {
    ElMessageBox.confirm('¿Registrar un plan de pagos para esta factura?', 'Confirmar', {
        confirmButtonText: 'Sí',
        cancelButtonText: 'Cancelar',
        type: 'warning',
    }).then(async () => {
        await InvoiceService.markPaymentPlan(invoice.value.id);
        ElMessage.success('Plan de pagos registrado');
        loadInvoice();
    }).catch(() => {});
};

const resolveClient = async () => {
    if (!selectedClient.value) {
        ElMessage.warning('Selecciona un cliente primero');
        return;
    }
    await InvoiceService.resolveClient(invoice.value.id, selectedClient.value);
    ElMessage.success('Cliente asociado correctamente');
    await loadInvoice();
    checkClientEmail();
};

const checkClientEmail = () => {
    if (!invoice.value?.clientResolved || invoice.value?.clientEmail) return;

    const clientId = invoice.value.clientId;
    const clientName = invoice.value.clientName;

    ElMessageBox.prompt(
        `El cliente <strong>${clientName}</strong> no tiene email registrado en ConnectUs.`
        + ' Sin email, el aviso del día 30 no podrá enviarse al cliente y se notificará a Bárbara.'
        + '<br><br>Introduce el email ahora, o cancela para ir a la ficha del cliente.',
        '⚠️ Cliente sin email',
        {
            confirmButtonText: 'Guardar email',
            cancelButtonText: 'Ir a la ficha del cliente',
            inputPlaceholder: 'email@ejemplo.com',
            inputType: 'email',
            inputValidator: (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'Email inválido',
            dangerouslyUseHTMLString: true,
            type: 'warning',
        }
    ).then(async ({ value }) => {
        if (!value) return;
        await ClientService.editClient(clientId, { email: value });
        ElMessage.success('Email guardado correctamente');
        loadInvoice();
    }).catch(() => {
        if (clientId) {
            router.push({ name: 'show-client', params: { id: clientId } });
        }
    });
};

const sendNotification = () => {
    uiStore.startLoading();
    const label = notificationButtonLabel.value;
    InvoiceService.sendNotification(invoice.value.id)
        .then(() => {
            ElMessage.success(`${label} enviado correctamente`);
            loadInvoice();
        })
        .catch(() => ElMessage.error(`Error al enviar: ${label}`))
        .finally(() => uiStore.stopLoading());
};

const notificationLevel = computed(() => {
    if (!invoice.value) return null;
    const days = dayjs().diff(dayjs(invoice.value.invoiceDate), 'day');
    if (days >= 90) return 'notice_90';
    if (days >= 60) return 'notice_60';
    if (days >= 35) return 'notice_35';
    if (days >= 30) return 'notice_30';
    return null;
});

const notificationButtonLabel = computed(() => {
    const labels = {
        notice_30: 'Enviar aviso 30 días',
        notice_35: 'Enviar aviso 35 días',
        notice_60: 'Enviar aviso 60 días',
        notice_90: 'Enviar aviso 90 días',
    };
    return labels[notificationLevel.value] ?? 'Enviar notificación';
});

const canSendNotification = computed(() => {
    if (!invoice.value || !notificationLevel.value) return false;
    if (invoice.value.status === 'paid') return false;
    if (['notice_60', 'notice_90'].includes(notificationLevel.value) && invoice.value.status === 'payment_plan') return false;
    const logs = invoice.value.notificationLogs ?? [];
    return !logs.some(l => !l.skipped && l.type === notificationLevel.value);
});

const notificationTooltip = computed(() => {
    if (!invoice.value) return '';
    if (invoice.value.status === 'paid') return 'La factura ya está pagada';
    if (!notificationLevel.value) return 'La factura aún no ha superado los 30 días';
    if (!canSendNotification.value) return 'La notificación de este nivel ya fue enviada';
    const days = dayjs().diff(dayjs(invoice.value.invoiceDate), 'day');
    return `Factura con ${days} días — ${notificationButtonLabel.value}`;
});

const triggerPdfUpload = () => {
    pdfFileInput.value?.click();
};

const pdfRequesting = ref(false);

const requestPdfFromHolded = () => {
    pdfRequesting.value = true;
    InvoiceService.requestPdf(invoice.value.id)
        .then(() => ElMessage.success('PDF solicitado. Estará disponible en breves momentos.'))
        .catch(() => ElMessage.error('Error al solicitar el PDF desde Holded'))
        .finally(() => { pdfRequesting.value = false; });
};

// FS-S01: el PDF ya no se sirve por ruta pública estática — se pide con el
// JWT (Api(), vía InvoiceService.downloadPdf) y se descarga el blob.
const downloadPdf = () => {
    pdfDownloading.value = true;
    InvoiceService.downloadPdf(invoice.value.id)
        .then((response) => {
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.target = '_blank';
            link.click();
            URL.revokeObjectURL(link.href);
        })
        .catch(() => ElMessage.error('Error al descargar el PDF de la factura'))
        .finally(() => { pdfDownloading.value = false; });
};

const onPdfSelected = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    uiStore.startLoading();
    InvoiceService.uploadPdf(invoice.value.id, file)
        .then(() => {
            ElMessage.success('PDF adjuntado correctamente');
            loadInvoice();
        })
        .catch(() => {
            ElMessage.error('Error al subir el PDF');
        })
        .finally(() => uiStore.stopLoading());
};

const statusLabel = (status) => {
    const map = { pending: 'Pendiente', paid: 'Pagada', payment_plan: 'Plan de pagos', overdue: 'Vencida', partial_payment: 'Pago parcial' };
    return map[status] ?? status;
};

const statusTag = (status) => {
    const map = { pending: 'warning', paid: 'success', payment_plan: 'info', overdue: 'danger', partial_payment: '' };
    return map[status] ?? '';
};

const daysFromDate = (dateStr) => {
    if (!dateStr) return '-';
    return Math.floor((Date.now() - new Date(dateStr)) / 86400000);
};

const daysUntilDue = (dateStr) => {
    if (!dateStr) return '-';
    return Math.ceil((new Date(dateStr) - Date.now()) / 86400000);
};

const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    const [intPart, decPart] = num.toFixed(2).split('.');
    return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + decPart + ' €';
};
</script>

<template>
    <div class="invoice-detail-page">
        <HeaderTitle title="Cobros" />

        <div class="d-flex flex-row justify-content-start mb-2 invoice-section-title">
            <div class="d-flex align-items-center">
                <font-awesome-icon icon="list" class="invoice-title-icon" />
                <div class="title-text">Detalle de factura</div>
            </div>
        </div>

        <div v-if="invoice" class="invoice-detail-container">
            <section class="invoice-identity-card">
                <div>
                    <span class="invoice-identity-card__label">Factura</span>
                    <h2>{{ invoice.invoiceNumber }}</h2>
                    <p>{{ invoice.clientName }}</p>
                </div>
                <div class="invoice-identity-card__actions">
                    <el-tag :type="statusTag(invoice.status)" size="large">{{ statusLabel(invoice.status) }}</el-tag>
                    <button class="btn invoice-back-button" type="button" @click="router.back()">
                        <font-awesome-icon icon="angle-left" />
                        Volver
                    </button>
                </div>
            </section>

            <section class="invoice-detail-kpis">
                <article class="invoice-kpi-card magenta">
                    <div class="invoice-kpi-icon"><font-awesome-icon icon="euro-sign" /></div>
                    <div>
                        <strong>{{ formatCurrency(invoice.pendingAmount) }}</strong>
                        <span>Pendiente</span>
                        <small>Por cobrar</small>
                    </div>
                </article>
                <article class="invoice-kpi-card red">
                    <div class="invoice-kpi-icon"><font-awesome-icon icon="stopwatch" /></div>
                    <div>
                        <strong>{{ daysUntilDue(invoice.dueDate) }} días</strong>
                        <span>Vencimiento</span>
                        <small>{{ invoice.dueDate?.substring(0, 10) || '-' }}</small>
                    </div>
                </article>
                <article class="invoice-kpi-card orange">
                    <div class="invoice-kpi-icon"><font-awesome-icon icon="user" /></div>
                    <div>
                        <strong>{{ invoice.clientResolved ? 'Sí' : 'No' }}</strong>
                        <span>Cliente resuelto</span>
                        <small>{{ invoice.clientName || '-' }}</small>
                    </div>
                </article>
                <article class="invoice-kpi-card teal">
                    <div class="invoice-kpi-icon"><font-awesome-icon icon="bell" /></div>
                    <div>
                        <strong>{{ invoice.notificationLogs?.length || invoice.notificationCount || 0 }}</strong>
                        <span>Notificaciones</span>
                        <small>Enviadas</small>
                    </div>
                </article>
            </section>

            <el-row :gutter="20">
                <!-- Datos de la factura -->
                <el-col :xs="24" :md="14">
                    <el-card class="invoice-panel mb-3">
                        <template #header><strong>Datos de la factura</strong></template>
                        <el-descriptions :column="2" border>
                            <el-descriptions-item label="Número">{{ invoice.invoiceNumber }}</el-descriptions-item>
                            <el-descriptions-item label="Estado">
                                <el-tag :type="statusTag(invoice.status)">{{ statusLabel(invoice.status) }}</el-tag>
                            </el-descriptions-item>
                            <el-descriptions-item label="Fecha emisión">{{
                                invoice.invoiceDate?.substring(0, 10) }}</el-descriptions-item>
                            <el-descriptions-item label="Vencimiento">{{ invoice.dueDate?.substring(0, 10)
                            }}</el-descriptions-item>
                            <el-descriptions-item label="Días desde emisión">
                                <span :class="{ 'text-danger fw-bold': daysFromDate(invoice.invoiceDate) >= 30 }">
                                    {{ daysFromDate(invoice.invoiceDate) }} días
                                </span>
                            </el-descriptions-item>
                            <el-descriptions-item label="Cliente resuelto">
                                <el-tag :type="invoice.clientResolved ? 'success' : 'warning'">
                                    {{ invoice.clientResolved ? 'Sí' : 'No' }}
                                </el-tag>
                                <el-tooltip v-if="invoice.clientResolved && !invoice.clientEmail"
                                    content="El cliente vinculado no tiene email en ConnectUs" placement="top">
                                    <el-tag type="warning" size="small" class="ms-1">Sin email</el-tag>
                                </el-tooltip>
                            </el-descriptions-item>
                            <el-descriptions-item label="Concepto">{{ invoice.description || '-'
                            }}</el-descriptions-item>
                            <el-descriptions-item label="Proyecto">{{ invoice.projectName || '-'
                            }}</el-descriptions-item>
                            <el-descriptions-item label="Cuenta">{{ invoice.account || '-' }}</el-descriptions-item>
                            <el-descriptions-item label="Forma de pago">{{ invoice.paymentMethod || '-'
                            }}</el-descriptions-item>
                            <el-descriptions-item label="Subtotal">{{ formatCurrency(invoice.subtotal) }}</el-descriptions-item>
                            <el-descriptions-item label="IVA">{{ formatCurrency(invoice.vatAmount) }}</el-descriptions-item>
                            <el-descriptions-item label="Total">
                                <strong>{{ formatCurrency(invoice.total) }}</strong>
                            </el-descriptions-item>
                            <el-descriptions-item label="Cobrado">{{ formatCurrency(invoice.paidAmount) }}</el-descriptions-item>
                            <el-descriptions-item label="Pendiente" :span="2">
                                <strong class="text-danger">{{ formatCurrency(invoice.pendingAmount) }}</strong>
                            </el-descriptions-item>
                            <el-descriptions-item v-if="invoice.paidAt" label="Fecha de pago">
                                {{ invoice.paidAt?.substring(0, 10) }}
                            </el-descriptions-item>
                            <el-descriptions-item v-if="invoice.notes" label="Notas" :span="2">
                                {{ invoice.notes }}
                            </el-descriptions-item>
                        </el-descriptions>
                    </el-card>

                    <!-- Historial de notificaciones -->
                    <el-card class="invoice-panel">
                        <template #header><strong>Historial de notificaciones</strong></template>
                        <div v-if="invoice.notificationLogs && invoice.notificationLogs.length > 0">
                            <table class="w-100 notif-table">
                                <thead>
                                    <tr>
                                        <th>Tipo</th>
                                        <th>Fecha</th>
                                        <th>Destinatario</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="log in invoice.notificationLogs" :key="log.id">
                                        <td>
                                            <el-tag size="small">{{ notificationLabels[log.type] ?? log.type
                                            }}</el-tag>
                                        </td>
                                        <td>{{ log.sentAt?.substring(0, 16).replace('T', ' ') }}</td>
                                        <td>{{ log.recipientName }} &lt;{{ log.recipientEmail }}&gt;</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div v-else class="text-muted">Aún no se ha enviado ninguna notificación.</div>
                    </el-card>
                </el-col>

                <!-- Acciones -->
                <el-col :xs="24" :md="10">
                    <el-card class="invoice-panel mb-3">
                        <template #header><strong>Acciones</strong></template>
                        <div class="invoice-action-stack">
                            <button class="btn btn_green invoice-action-button" type="button"
                                :disabled="invoice.status === 'paid'" @click="markPaid">
                                <font-awesome-icon icon="check" />
                                Marcar como pagada
                            </button>
                            <button class="btn btn_yellow invoice-action-button" type="button"
                                :disabled="invoice.status === 'paid' || invoice.status === 'payment_plan'"
                                @click="markPaymentPlan">
                                <font-awesome-icon icon="arrows-rotate" />
                                Registrar plan de pagos
                            </button>
                            <el-tooltip :content="notificationTooltip" placement="left">
                                <span>
                                    <button class="btn invoice-action-button btn--notify" type="button"
                                        :disabled="!canSendNotification" @click="sendNotification">
                                        <font-awesome-icon icon="bell" />
                                        {{ notificationButtonLabel }}
                                    </button>
                                </span>
                            </el-tooltip>
                        </div>
                    </el-card>

                    <el-card class="invoice-panel mb-3" v-if="!invoice.clientResolved">
                        <template #header><strong>Resolver cliente</strong></template>
                        <el-select v-model="selectedClient" placeholder="Selecciona cliente..." filterable clearable
                            class="w-100 invoice-panel-control">
                            <el-option v-for="c in clients" :key="c['@id']" :label="c.name" :value="c['@id']" />
                        </el-select>
                        <button class="btn btn_blue invoice-action-button" type="button" @click="resolveClient">
                            Asociar cliente
                        </button>
                    </el-card>

                    <el-card v-if="invoice.clientResolved && !invoice.clientEmail"
                        class="invoice-panel mb-3 invoice-panel--warning">
                        <template #header>
                            <strong>⚠️ Email de cliente no registrado</strong>
                        </template>
                        <p class="mb-2 small text-muted">
                            El cliente vinculado no tiene email en ConnectUs.<br>
                            El aviso del día 30 no se podrá enviar al cliente hasta que se añada.
                        </p>
                        <button class="btn invoice-action-button btn-warning-email" type="button"
                            @click="checkClientEmail">
                            <font-awesome-icon icon="envelope" />
                            Añadir email al cliente
                        </button>
                    </el-card>

                    <el-card class="invoice-panel">
                        <template #header><strong>PDF de la factura</strong></template>
                        <div v-if="invoice.hasPdf" class="invoice-panel-control">
                            <button type="button" class="btn invoice-pdf-link" :disabled="pdfDownloading"
                                @click="downloadPdf">
                                <font-awesome-icon icon="eye" />
                                {{ pdfDownloading ? 'Descargando...' : 'Ver PDF adjunto' }}
                            </button>
                        </div>
                        <template v-else>
                            <div v-if="invoice.holdedDocumentId" class="invoice-panel-control">
                                <div class="text-muted small mb-2">PDF disponible en Holded, pendiente de descarga.</div>
                                <button class="btn invoice-action-button invoice-btn--holded" type="button"
                                    :disabled="pdfRequesting" @click="requestPdfFromHolded">
                                    <font-awesome-icon icon="arrows-rotate" :class="{ 'fa-spin': pdfRequesting }" />
                                    {{ pdfRequesting ? 'Solicitando...' : 'Obtener PDF de Holded' }}
                                </button>
                            </div>
                            <div v-else class="text-muted small invoice-panel-control">Sin PDF adjunto</div>
                        </template>
                        <input ref="pdfFileInput" type="file" accept="application/pdf" class="d-none"
                            @change="onPdfSelected" />
                        <button class="btn btn_red invoice-action-button" type="button" @click="triggerPdfUpload">
                            <font-awesome-icon icon="upload" />
                            {{ invoice.hasPdf ? 'Reemplazar PDF' : 'Adjuntar PDF' }}
                        </button>
                    </el-card>
                </el-col>
            </el-row>
        </div>

        <div v-else class="text-center mt-5">
            <span class="text-muted">Cargando...</span>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.invoice-detail-page {
    padding: 0 22px 32px;
}

:deep(#header-title) {
    margin-bottom: 0;
}

.invoice-detail-container {
    max-width: 1680px;
    margin: 0 auto;
    padding-top: 10px;
}

.invoice-section-title {
    max-width: 1680px;
    margin-left: auto;
    margin-right: auto;
}

.invoice-title-icon {
    width: 30px;
    height: 30px;
    color: $bg-blue;
}

.invoice-identity-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    margin-bottom: 18px;
    padding: 22px 24px;
    background: #fff;
    border: 1px solid #e5e7ef;
    border-radius: 18px;
    box-shadow: 0 8px 20px rgba(31, 41, 55, 0.08);
}

.invoice-identity-card__label {
    display: block;
    margin-bottom: 2px;
    color: #667085;
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
}

.invoice-identity-card h2 {
    margin: 0;
    color: #3445f4;
    font-size: 26px;
    font-weight: 700;
}

.invoice-identity-card p {
    margin: 4px 0 0;
    color: #4b5563;
}

.invoice-identity-card__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
}

.invoice-identity-card__actions svg,
.invoice-action-stack svg {
    margin-right: 7px;
}

.invoice-back-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 136px;
    height: 3em;
    border: 1px solid #d6d9e6;
    background: #fff;
    color: #667085;
    font-weight: 700;
    text-transform: none;
}

.invoice-back-button:hover {
    background: #f8f9ff;
    color: #474CE6;
}

.invoice-detail-kpis {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
    margin-bottom: 18px;
}

.invoice-kpi-card {
    display: flex;
    align-items: center;
    gap: 18px;
    min-height: 116px;
    padding: 18px;
    background: #fff;
    border: 1px solid #e5e7ef;
    border-radius: 12px;
    box-shadow: 0 8px 20px rgba(31, 41, 55, 0.08);
}

.invoice-kpi-icon {
    display: grid;
    place-items: center;
    flex: 0 0 auto;
    width: 70px;
    height: 70px;
    border-radius: 8px;
    color: #fff;
    font-size: 30px;
}

.invoice-kpi-card.magenta .invoice-kpi-icon {
    background: linear-gradient(135deg, #ff0f78 0%, #f4215f 100%);
}

.invoice-kpi-card.red .invoice-kpi-icon {
    background: linear-gradient(135deg, #ff2936 0%, #ef233c 100%);
}

.invoice-kpi-card.orange .invoice-kpi-icon {
    background: linear-gradient(135deg, #ffaf00 0%, #fb8e00 100%);
}

.invoice-kpi-card.teal .invoice-kpi-icon {
    background: linear-gradient(135deg, #11bfc6 0%, #008ea0 100%);
}

.invoice-kpi-card strong {
    display: block;
    font-size: 24px;
    line-height: 1.1;
    color: #3445f4;
    white-space: nowrap;
}

.invoice-kpi-card span,
.invoice-kpi-card small {
    display: block;
    color: #4b5563;
    max-width: 210px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.invoice-panel {
    border: 1px solid #e5e7ef;
    border-radius: 16px;
    box-shadow: 0 8px 20px rgba(31, 41, 55, 0.08);
    overflow: hidden;
}

.invoice-panel :deep(.el-card__header) {
    color: #1f2937;
    background: #fff;
}

.invoice-panel :deep(.el-card__body) {
    padding: 20px;
}

.invoice-panel :deep(.el-descriptions) {
    margin: 0;
}

.invoice-action-stack {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.invoice-panel-control {
    margin-bottom: 14px;
}

.invoice-action-button,
.invoice-pdf-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 3em;
    margin-left: 0;
    font-weight: 800;
    text-align: center;
}

.invoice-action-button {
    border: 0;
}

.invoice-action-button:disabled {
    cursor: not-allowed;
    opacity: 0.45;
}

.invoice-pdf-link {
    color: #474CE6;
    background: #f8f9ff;
    border: 1px solid #d6d9e6;
    text-decoration: none;
}

.invoice-pdf-link:hover {
    color: #fff;
    background: #474CE6;
    text-decoration: none;
}

.invoice-pdf-link svg {
    margin-right: 7px;
}

.notif-table {
    border-collapse: collapse;
    font-size: 13px;

    th,
    td {
        padding: 6px 10px;
        border-bottom: 1px solid #eee;
        text-align: left;
    }

    th {
        font-weight: 600;
        color: #666;
    }
}

@media (max-width: 1200px) {
    .invoice-detail-kpis {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 767px) {
    .invoice-detail-page {
        padding: 0 14px 24px;
    }

    .invoice-identity-card,
    .invoice-identity-card__actions {
        align-items: stretch;
        flex-direction: column;
    }

    .invoice-back-button {
        width: 100%;
    }

    .invoice-detail-kpis {
        grid-template-columns: 1fr;
    }

    .invoice-kpi-card {
        align-items: center;
        flex-direction: row;
        min-height: 0;
    }

    .invoice-kpi-icon {
        width: 52px;
        height: 52px;
        font-size: 22px;
    }

    .invoice-kpi-card strong {
        font-size: 22px;
    }

    :deep(.el-descriptions__body .el-descriptions__table) {
        display: block;
    }

    :deep(.el-descriptions__body .el-descriptions__table tbody),
    :deep(.el-descriptions__body .el-descriptions__table tr),
    :deep(.el-descriptions__body .el-descriptions__table th),
    :deep(.el-descriptions__body .el-descriptions__table td) {
        display: block;
        width: 100% !important;
    }

    .notif-table,
    .notif-table thead,
    .notif-table tbody,
    .notif-table tr,
    .notif-table th,
    .notif-table td {
        display: block;
        width: 100%;
    }

    .notif-table thead {
        display: none;
    }

    .notif-table td {
        border-bottom: none;
        padding: 5px 0;
    }

    .notif-table tr {
        padding: 10px 0;
        border-bottom: 1px solid #eee;
    }
}

.btn--notify {
    color: #fff;
    background: #2563eb;
    border: 1px solid #2563eb;

    &:hover:not(:disabled) {
        background: #1d4ed8;
        border-color: #1d4ed8;
        color: #fff;
    }

    &:disabled {
        background: #93c5fd;
        border-color: #93c5fd;
        cursor: not-allowed;
    }
}

.invoice-panel--warning {
    border-color: #f59e0b !important;

    :deep(.el-card__header) {
        background: #fffbeb;
        color: #92400e;
        border-bottom-color: #fde68a;
    }
}

.btn-warning-email {
    color: #92400e;
    border: 1px solid #f59e0b;
    background: #fffbeb;

    &:hover {
        color: #fff;
        background: #f59e0b;
        border-color: #f59e0b;
    }
}

.invoice-btn--holded {
    color: #065f46;
    border: 1px solid #6ee7b7;
    background: #ecfdf5;

    &:hover:not(:disabled) {
        color: #fff;
        background: #059669;
        border-color: #059669;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}
</style>
